import React, { useState, useEffect } from 'react';
import { User, DayOfWeek } from '../types';
import { apiClient, ApiClientError } from '../services/api-client';
import { useLoading } from '../hooks/useLoading';
import ErrorMessage from './ErrorMessage';
import LoadingSpinner from './LoadingSpinner';
import './UserManagement.css';

interface UserManagementProps {}

const UserManagement: React.FC<UserManagementProps> = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { loading, withLoading } = useLoading();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  // Load users on component mount
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const loadedUsers = await withLoading('loadUsers', () => apiClient.getUsers());
      setUsers(loadedUsers);
      setError(null);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError(err instanceof ApiClientError ? err.message : 'ユーザーの読み込みに失敗しました');
    }
  };

  const handleAddUser = async (userData: { name: string; availableDays: DayOfWeek[] }) => {
    try {
      const newUser = await withLoading('createUser', () => 
        apiClient.createUser(userData.name, userData.availableDays)
      );
      setUsers(prev => [...prev, newUser]);
      setShowAddForm(false);
      setError(null);
    } catch (err) {
      console.error('Failed to create user:', err);
      setError(err instanceof ApiClientError ? err.message : 'ユーザーの作成に失敗しました');
    }
  };

  const handleUpdateUser = async (updatedUser: User) => {
    try {
      const updated = await withLoading('updateUser', () =>
        apiClient.updateUser(updatedUser.id, {
          name: updatedUser.name,
          availableDays: updatedUser.availableDays
        })
      );
      setUsers(prev => prev.map(user => 
        user.id === updated.id ? updated : user
      ));
      setEditingUser(null);
      setError(null);
    } catch (err) {
      console.error('Failed to update user:', err);
      setError(err instanceof ApiClientError ? err.message : 'ユーザーの更新に失敗しました');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('この利用者を削除してもよろしいですか？')) {
      try {
        await withLoading('deleteUser', () => apiClient.deleteUser(userId));
        setUsers(prev => prev.filter(user => user.id !== userId));
        setError(null);
      } catch (err) {
        console.error('Failed to delete user:', err);
        setError(err instanceof ApiClientError ? err.message : 'ユーザーの削除に失敗しました');
      }
    }
  };

  const getDayName = (day: DayOfWeek): string => {
    const dayNames = {
      [DayOfWeek.Monday]: '月',
      [DayOfWeek.Tuesday]: '火',
      [DayOfWeek.Wednesday]: '水',
      [DayOfWeek.Thursday]: '木',
      [DayOfWeek.Friday]: '金',
      [DayOfWeek.Saturday]: '土',
      [DayOfWeek.Sunday]: '日'
    };
    return dayNames[day];
  };

  return (
    <div className="user-management">
      <div className="user-header">
        <h2>利用者管理</h2>
        <button 
          onClick={() => setShowAddForm(true)}
          className="add-user-btn"
          disabled={loading.loadUsers}
        >
          利用者追加
        </button>
      </div>

      {error && (
        <ErrorMessage 
          message={error} 
          onDismiss={() => setError(null)} 
        />
      )}

      {loading.loadUsers && (
        <LoadingSpinner message="利用者情報を読み込み中..." />
      )}

      <div className="user-list">
        {users.length === 0 ? (
          <div className="empty-state">
            <p>登録された利用者がありません</p>
            <button onClick={() => setShowAddForm(true)}>
              最初の利用者を追加
            </button>
          </div>
        ) : (
          <div className="user-grid">
            {users.map(user => (
              <div key={user.id} className="user-card">
                <div className="user-info">
                  <h3>{user.name}</h3>
                  <div className="available-days">
                    <span className="label">参加可能曜日:</span>
                    <div className="days">
                      {user.availableDays.map(day => (
                        <span key={day} className="day-badge">
                          {getDayName(day)}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="user-dates">
                    <small>
                      作成: {user.createdAt.toLocaleDateString('ja-JP')}
                    </small>
                  </div>
                </div>
                
                <div className="user-actions">
                  <button 
                    onClick={() => setEditingUser(user)}
                    className="edit-btn"
                  >
                    編集
                  </button>
                  <button 
                    onClick={() => handleDeleteUser(user.id)}
                    className="delete-btn"
                  >
                    削除
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAddForm && (
        <UserForm
          onSave={handleAddUser}
          onCancel={() => setShowAddForm(false)}
        />
      )}

      {editingUser && (
        <UserForm
          user={editingUser}
          onSave={handleUpdateUser}
          onCancel={() => setEditingUser(null)}
        />
      )}
    </div>
  );
};

// User form component
interface UserFormProps {
  user?: User;
  onSave: (user: any) => void;
  onCancel: () => void;
}

const UserForm: React.FC<UserFormProps> = ({ user, onSave, onCancel }) => {
  const [name, setName] = useState(user?.name || '');
  const [availableDays, setAvailableDays] = useState<DayOfWeek[]>(
    user?.availableDays || []
  );
  const [errors, setErrors] = useState<string[]>([]);

  const allDays = [
    { value: DayOfWeek.Monday, label: '月曜日' },
    { value: DayOfWeek.Tuesday, label: '火曜日' },
    { value: DayOfWeek.Wednesday, label: '水曜日' },
    { value: DayOfWeek.Thursday, label: '木曜日' },
    { value: DayOfWeek.Friday, label: '金曜日' },
    { value: DayOfWeek.Saturday, label: '土曜日' },
    { value: DayOfWeek.Sunday, label: '日曜日' }
  ];

  const handleDayToggle = (day: DayOfWeek) => {
    if (availableDays.includes(day)) {
      setAvailableDays(availableDays.filter(d => d !== day));
    } else {
      setAvailableDays([...availableDays, day]);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: string[] = [];
    
    if (!name.trim()) {
      newErrors.push('利用者名を入力してください');
    }
    
    if (availableDays.length === 0) {
      newErrors.push('参加可能曜日を少なくとも1つ選択してください');
    }
    
    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const userData = user ? 
      { ...user, name: name.trim(), availableDays } :
      { name: name.trim(), availableDays };
    
    onSave(userData);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h3>{user ? '利用者編集' : '利用者追加'}</h3>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>利用者名 *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="利用者名を入力"
            />
          </div>
          
          <div className="form-group">
            <label>参加可能曜日 *</label>
            <div className="day-checkboxes">
              {allDays.map(day => (
                <label key={day.value} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={availableDays.includes(day.value)}
                    onChange={() => handleDayToggle(day.value)}
                  />
                  {day.label}
                </label>
              ))}
            </div>
          </div>
          
          {errors.length > 0 && (
            <div className="error-list">
              {errors.map((error, index) => (
                <div key={index} className="error">{error}</div>
              ))}
            </div>
          )}
          
          <div className="modal-actions">
            <button type="submit">
              {user ? '更新' : '追加'}
            </button>
            <button type="button" onClick={onCancel}>
              キャンセル
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserManagement;